import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import useAxios from 'axios-hooks';
import isEmpty from 'lodash.isempty';
import uniq from 'lodash.uniq';
import { startOfDay, endOfDay, subDays } from 'date-fns';

import { short, long } from '../common/formatting/date';
import groupBy from 'lodash.groupby';
import orderBy from 'lodash.orderby';
import {
	CheckboxDropdown,
	DateRangePickerDropdown,
	Switch,
} from '../../../components';

const VITAL_NAME_LABELS = {
	steps: 'Steps',
};

const TYPE_LABELS = {
	min: 'Daily Min',
	max: 'Daily Max',
	avg: 'Daily Average',
};

const PatientDetails = ({ patient, userId }) => {
	const [selectedTypes, setSelectedTypes] = useState([]);
	const [startDate, setStartDate] = useState(
		subDays(startOfDay(new Date()), 7)
	);
	const [endDate, setEndDate] = useState(endOfDay(new Date()));
	const [includeNormal, setIncludeNormal] = useState(true);

	const [
		{ data: aggData, loading: aggDataLoading /* error */ },
		loadAggregateData,
	] = useAxios(
		{
			url: `/providers/${userId}/vital-tracker-patient-aggregate`,
		},
		{ manual: true, autoCancel: false }
	);
	const [
		{ data: detailData, loading: detailDataLoading /* error */ },
		loadRPMDetailData,
	] = useAxios(
		{
			url: `/providers/${userId}/vital-tracker-dashboard-patient-detail`,
		},
		{ manual: true, autoCancel: false }
	);

	useEffect(() => {
		if (patient) {
			const date_start = subDays(startOfDay(new Date()), 7);
			const date_end = endOfDay(new Date());
			setStartDate(date_start);
			setEndDate(date_end);
			setSelectedTypes([]);
		}
	}, [patient]);

	useEffect(() => {
		if (patient) {
			loadAggregateData({
				params: {
					patientId: patient.patientId,
					date_end: endDate,
					date_start: startDate,
				},
			});
			loadRPMDetailData({
				params: {
					patient_id: patient.patientId,
					date_end: endDate,
					date_start: startDate,
					show_all: true,
				},
			});
		}
	}, [patient, startDate, endDate]);

	useEffect(() => {
		if (
			!aggDataLoading &&
			Array.isArray(aggData) &&
			!detailDataLoading &&
			Array.isArray(detailData)
		) {
			setSelectedTypes(
				uniq([
					...aggData.map(({ vital_name }) => vital_name),
					...detailData.map(({ vital_name }) => vital_name),
				]).sort()
			);
		}
	}, [aggData, aggDataLoading, detailData, detailDataLoading]);

	const handleRangeChosen = ({ startDate, endDate }) => {
		setStartDate(startDate);
		setEndDate(endDate);
	};

	const handlePrintDetails = () => {
		document.getElementById('body').classList.add('patient-details');
		window.print();
		document.getElementById('body').classList.remove('patient-details');
	};

	let normalizedData = {};
	if (Array.isArray(aggData) && aggData.length > 0) {
		normalizedData = groupBy(
			orderBy(aggData, 'start_dttm', 'desc'),
			'vital_name'
		);
	}
	if (Array.isArray(detailData) && detailData.length > 0) {
		const normalizedDetailData = groupBy(
			orderBy(detailData, 'vital_dttm', 'desc'),
			'vital_name'
		);
		Object.keys(normalizedDetailData).forEach((nDDKey) => {
			if (normalizedData[nDDKey]) {
				normalizedData[nDDKey] = [
					...normalizedData[nDDKey],
					...normalizedDetailData[nDDKey],
				];
			} else {
				normalizedData[nDDKey] = normalizedDetailData[nDDKey];
			}
		});
	}

	return (
		<div className="rpm-patient-details">
			{!patient && <div className="m-lg">Choose a patient on the left</div>}
			{(aggDataLoading || detailDataLoading) && (
				<div className="m-lg">Loading data...</div>
			)}
			{!aggDataLoading && !detailDataLoading && patient && (
				<div className="rpm-patient-details-header row">
					<div className="col-lg-4">
						<h1>
							{patient.name_first} {patient.name_last}
						</h1>
						<h2>
							<small>
								DOB:{' '}
								{!isEmpty(patient.birth_dttm) &&
									short(patient.birth_dttm.split('T')[0])}
							</small>
						</h2>
					</div>
					<div className="col-lg-4 pt-lg text-center">
						<DateRangePickerDropdown
							startDate={startDate}
							endDate={endDate}
							onRangeChosen={handleRangeChosen}
							maxDate={new Date()}
							minDate={subDays(new Date(), 90)}
							position="center"
						/>
					</div>
					<div className="col-lg-4 pt-lg text-right">
						<button className="btn btn-default mr" onClick={handlePrintDetails}>
							<em className="fa fa-print"></em>
						</button>
						<CheckboxDropdown
							title="Filter Data Types"
							options={Object.keys(normalizedData).map((key) => ({
								value: key,
								label: key,
							}))}
							right
							selectedValues={selectedTypes}
							onChange={setSelectedTypes}
						/>
						<div className="mt">
							<Switch
								on={includeNormal}
								onChange={() => setIncludeNormal(!includeNormal)}
							/>{' '}
							Show Normal Values
						</div>
					</div>
				</div>
			)}
			{!aggDataLoading &&
				!detailDataLoading &&
				Array.isArray(aggData) &&
				isEmpty(aggData) &&
				Array.isArray(detailData) &&
				isEmpty(detailData) && (
					<div className="rpm-patient-details-data">
						No data found for selected patient
					</div>
				)}
			{!aggDataLoading && !detailDataLoading && !isEmpty(normalizedData) && (
				<div className="rpm-patient-details-data">
					<h3>RPM Detail</h3>
					{Object.keys(normalizedData)
						.filter((vitalName) => selectedTypes.includes(vitalName))
						.map((vitalName) => (
							<React.Fragment key={vitalName}>
								<h4>{VITAL_NAME_LABELS[vitalName] || vitalName}</h4>
								<div className="table-responsive">
									<table className="table table-bordered">
										<thead>
											<tr>
												<td>Date</td>
												<td>Value</td>
												<td>UOM</td>
												<td>Type</td>
												<td>Source</td>
											</tr>
										</thead>
										<tbody>
											{normalizedData[vitalName]
												.filter(({ vital_dttm, out_of_range_ind }) =>
													vital_dttm && !includeNormal
														? out_of_range_ind == 1
														: true
												)
												.map((vitalData) => (
													<tr
														key={`${vitalData.patientId}-${vitalData.vital_id}`}
													>
														<td>
															{vitalData.vital_dttm
																? long(vitalData.vital_dttm)
																: short(vitalData.start_dttm)}
														</td>
														<td>{vitalData.value}</td>
														<td>{vitalData.unit}</td>
														<td>
															{vitalData.vital_dttm
																? vitalData.out_of_range_ind == 1
																	? 'Out Of Range'
																	: 'Normal'
																: TYPE_LABELS[vitalData.aggregate_type] ||
																  vitalData.aggregate_type}
														</td>
														<td>{vitalData.data_source}</td>
													</tr>
												))}
										</tbody>
									</table>
								</div>
							</React.Fragment>
						))}
				</div>
			)}
		</div>
	);
};

PatientDetails.propTypes = {
	patient: PropTypes.object,
	userId: PropTypes.number.isRequired,
};

export default PatientDetails;
